import type { ReactNode } from "react";

// EmptyState — DS §4.16 (.empty). 1px SOLID hairline, no illustration.
//
// Solid, not dashed: a dashed border is the drop-zone / unfilled-slot idiom, so
// it read as scaffolding on states that are finished and deliberate (the
// couples welcome is the clearest case — "your practice is set up" inside a box
// that looks unbuilt). Changed 2026-08-31, applies to every empty state.

export interface EmptyStateProps {
    eyebrow?: string;
    title: string;
    /** Heading level + type ramp for the title. Defaults to `h3`, because an
     *  empty state normally sits INSIDE a page that already owns the h1 (every
     *  other consumer does). Pass `h1` when the empty state IS the page: the
     *  couples dashboard hides its header when there are no couples, so without
     *  this the document would start at h3 with no h1 above it. */
    titleAs?: "h1" | "h3";
    /** Supporting copy — a string, or composed nodes for multi-line bodies. */
    body?: ReactNode;
    primary?: ReactNode;
    secondary?: ReactNode;
}

/** The h1 ramp is the DS's own --type-h1 (500 30px/1.15 serif) + display
 *  tracking, so the page-level empty state matches a real page title rather
 *  than inventing a third heading size.
 *
 *  These carry NO margin. Spacing is `gap` on the flex column below, for two
 *  reasons: it comes off the DS --space-* scale rather than Tailwind's numeric
 *  one (the old `mb-2` was 10px, not a step on 4/8/12/16/24/48), and a gap
 *  cannot be overridden by a host app's element reset. portal-www's main.css
 *  carries an UNLAYERED `h1 { margin: 0 }`, and unlayered declarations outrank
 *  anything in @layer utilities — so a `mb-*` utility on the h1 silently did
 *  nothing, which is exactly the bug this replaced. */
const TITLE_CLASS: Record<"h1" | "h3", string> = {
    h1: "[font:var(--type-h1)] tracking-[-0.015em] text-primary",
    h3: "font-serif text-xl font-medium leading-[1.3] tracking-[-0.005em] text-primary",
};

export function EmptyState({
    eyebrow,
    title,
    titleAs = "h3",
    body,
    primary,
    secondary,
}: EmptyStateProps) {
    const Title = titleAs;
    return (
        <div className="flex flex-col items-center gap-[var(--space-24)] rounded-[var(--radius-lg)] border border-solid border-border bg-surface px-8 py-16 text-center">
            {/* Eyebrow, title and body are ONE unit at the tighter gap; the
                actions sit at the outer gap, so the reader groups the message
                and reads the CTA as the separate thing to do. */}
            <div className="flex flex-col items-center gap-[var(--space-16)]">
                {eyebrow && <div className="dova-eyebrow">{eyebrow}</div>}
                <Title className={TITLE_CLASS[titleAs]}>{title}</Title>
                {body && (
                    <p className="mx-auto max-w-[380px] font-sans text-base leading-[1.5] text-secondary">
                        {body}
                    </p>
                )}
            </div>
            {(primary || secondary) && (
                <div className="flex items-center justify-center gap-2">
                    {primary}
                    {secondary}
                </div>
            )}
        </div>
    );
}
